import cn from 'classnames';
import dayjs from 'dayjs';
import { useState, useEffect } from 'react';

import globalStyles from '../../../styles/pages/index.module.scss';
import CategoriesAside from '../../components/CategoriesAside/CategoriesAside';
import CategoriesBanner from '../../components/CategoriesBanner/CategoriesBanner';
import CategoriesSelect from '../../components/CategoriesSelect/CategoriesSelect';
import CustomMetaHead from '../../components/CustomMetaHead';
import DeliveryBanner from '../../components/DeliveryBanner/DeliveryBanner';
import FavoritesAside from '../../components/FavoritesAside/FavoritesAside';
import Footer from '../../components/Footer/Footer';
import Header from '../../components/Header/Header';
import Navigation from '../../components/Navigation/Navigation';
import OfferList from '../../components/ProductsMenu/OfferList';
import ProductMenu from '../../components/ProductsMenu/ProductMenu';
import META_DATA from '../../constants/metaData';
import CartStore from '../../store/cart';
import CategoryStore from '../../store/categories';
import DeliveryMode from '../../store/deliveryMode';
import ProductsCollection from '../../store/productsCollection';
import SortBy from '../../store/productsSort';
import BANNER_SORT_OPTIONS from '../../utils/bannerOptions';
import { getOrFetchFooterLinks } from '../../utils/footerLinks';
import { getOrFetchNavigationLinks } from '../../utils/navigationLinks';
import { serverSideOffersFetchingFromFile } from '../../utils/server-side/offers';

import pageStyles from './offers.module.scss';

export default function Offers({ navigation, footerLinks, offers, meta }) {
  const [showBySelection, setShowBySelection] = useState(BANNER_SORT_OPTIONS.catalog);
  const [numOfProducts, setNumOfProducts] = useState(0);
  const [deliveryMode, setDeliveryMode] = useState('');
  const [waveSpecialProductsGrouped, setWaveSpecialProductsGrouped] = useState([]);
  const [productCollections, setProductCollections] = useState();
  const [categories, setCategories] = useState();
  const [offersByCollectionType, setOffersByCollectionType] = useState([]);
  const [isCartVisible, setIsCartVisible] = useState(false);
  const slug = ['offers'];

  function findWaveProductsOffers(parentCategories, products) {
    if (!parentCategories.length) return;
    const productsInCategory = products.filter(
      p => p.offerType === 'web-only-amount' || p.offerType === 'web-only-percentage'
    );
    setWaveSpecialProductsGrouped(productsInCategory);
  }

  useEffect(() => {
    const subscriptionDeliveryMode = DeliveryMode.subscribe(setDeliveryMode);
    const productsCollection$ = ProductsCollection.subscribe(setProductCollections);
    const isCartVisible$ = CartStore.getIsVisble$().subscribe(setIsCartVisible);
    const sortBy$ = SortBy.subscribe(setShowBySelection);
    const categories$ = CategoryStore.subscribe(setCategories);
    return () => {
      sortBy$.unsubscribe();
      categories$.unsubscribe();
      isCartVisible$.unsubscribe();
      productsCollection$.unsubscribe();
      subscriptionDeliveryMode.unsubscribe();
    };
  }, []);

  useEffect(() => {
    if (!productCollections) return;
    if (!categories) return;
    const service = DeliveryMode.getValue();
    const collectionType = DeliveryMode.getCollectionAsNumber(service);
    const mainCollectionType = collectionType.find(c => c === process.env.defaultCollectionType || c === '751');
    const offersInCollection = offers.filter(offer => offer.collectionTypes.includes(parseInt(mainCollectionType)));
    setOffersByCollectionType(offersInCollection);
    const allProductsCollection = ProductsCollection.getProductsForCollections(collectionType);
    findWaveProductsOffers(categories, allProductsCollection);
  }, [productCollections, categories]);

  return (
    <>
      <CustomMetaHead meta={meta} />
      <div className={globalStyles.headerContainer}>
        <Header navigation={navigation} />
      </div>

      <Navigation navigation={navigation} />
      <DeliveryBanner />

      <CategoriesSelect slug={slug} />

      <main
        className={cn(pageStyles.main, {
          [pageStyles.spaceOpenCart]: isCartVisible,
        })}
      >
        <div className={cn([pageStyles.mainLeft, pageStyles.listBanner])}>
          <div className={cn([pageStyles.sticky])}>
            <CategoriesBanner
              onChangeOrderBy={e => SortBy.update(e.target.value)}
              {...{
                showBySelection,
                numOfProducts,
                categoryName: 'Προσφορές',
                subCategoryName: null,
                slug,
                deliveryMode,
                isProductList: false,
              }}
            />
          </div>

          <section className={pageStyles.mainContent}>
            <div className={pageStyles.hideOnSmallScreen}>
              <div className={cn([pageStyles.sticky, pageStyles.categoriesAsideTop])}>
                <FavoritesAside />
                <CategoriesAside slug={slug} />
              </div>
            </div>
            <div className={pageStyles.offerListWrapper}>
              <div className={pageStyles.offerListContainer}>
                <OfferList
                  offers={offersByCollectionType}
                  categories={categories}
                  waveSpecialProductsGrouped={waveSpecialProductsGrouped}
                  showBySelection={showBySelection}
                />
              </div>
              <ProductMenu
                isSecondLevelPage={false}
                isOfferListPage
                categories={categories}
                productCollections={productCollections}
                slug={slug}
                setNumOfProducts={setNumOfProducts}
                deliveryMode={deliveryMode}
                offers={offers}
                showBySelection={showBySelection}
              />
            </div>
          </section>
        </div>
      </main>
      <Footer footerLinks={footerLinks} />
    </>
  );
}

export async function getStaticProps() {
  const [{ data: navigation }, footerLinks] = await Promise.all([getOrFetchNavigationLinks(), getOrFetchFooterLinks()]);
  const offersAll = serverSideOffersFetchingFromFile();
  const nowUnix = dayjs().unix();
  const offers = offersAll.filter(
    offer => offer.enabled && offer.available && offer.beginDate < nowUnix && offer.endDate > nowUnix
  );

  const meta = META_DATA[process.env.appName].offers;

  return {
    props: { navigation, footerLinks, offers, meta },
  };
}
